import { api } from "./api";

// Cards that can appear in the chat panel during a call, described for the voice assistant (skill "Pengisian Kartu Terpandu")
// and applied through the generic `submit_card` tool — the same actions the card buttons perform.
const opt = (items) => items.map(([id, label, hint]) => `${id}${label && label !== id ? ` = ${label}` : ""}${hint ? ` (${hint})` : ""}`).join(", ");

export function describeCard(m) {
  const pt = m.pending_tool;
  if (pt?.kind === "video") {
    const models = (pt.options || []).filter((o) => o.available !== false);
    return `[Chat panel] CARD_APPEARED type=video message_id=${m.id} title="Buat Video"${pt.brief?.title ? ` brief="${pt.brief.title}"` : ""}\nfields (in order):\n`
      + `- model: ${models.map((o) => `${o.tier} = ${o.label}${o.credits ? ` (${o.credits} kredit)` : ""}`).join(", ")}\n`
      + `- duration (seconds): ${[...new Set(models.flatMap((o) => o.durations || [8]))].join(", ")}\n`
      + `- resolution: ${[...new Set(models.flatMap((o) => o.resolutions || ["720p"]))].join(", ")}\n`
      + `- aspect: ${[...new Set(models.flatMap((o) => o.aspects || ["16:9", "9:16"]))].join(", ")}\n- with_audio: true/false`;
  }
  if (pt && (pt.kind === "image" || pt.kind === "image_edit")) {
    const models = (pt.models || pt.options || []).filter((o) => o.available !== false);
    return `[Chat panel] CARD_APPEARED type=image message_id=${m.id} title="${pt.kind === "image_edit" ? "Edit gambar" : "Buat gambar"}"${pt.brief?.title ? ` brief="${pt.brief.title}"` : ""}\nfields (in order):\n`
      + (models.length ? `- model: ${models.map((o) => `${o.id || o.tier} = ${o.label}`).join(", ")}\n` : "")
      + `- aspect: ${opt([["1:1", "Persegi"], ["16:9", "Lanskap"], ["9:16", "Potret"], ["4:3", "4:3"], ["3:4", "3:4"], ["21:9", "Ultra-lebar"]])}\n`
      + `- quality: ${opt([["hemat", "Hemat", "1K draf"], ["standar", "Standar", "2K"], ["tinggi", "Tinggi", "4K detail"]])}`;
  }
  if (m.tool === "revision_plan" && m.plan) {
    const p = m.plan;
    const stages = (p.affected || []).map((a) => `tahap ${a.no} «${a.title}»${a.kind === "ulang" ? " (ulang)" : ""}`).join(", ");
    return `[Chat panel] CARD_APPEARED type=revision_plan message_id=${m.id} title="Rencana Revisi"\nsummary: ${p.summary}${stages ? `\naffected stages: ${stages}` : p.next_version ? `\nresult: document saved as v${p.next_version}` : ""}${p.est_credits ? `\nestimate: ≈ ${p.est_credits} kredit` : ""}${p.question ? `\nclarifying question to ask: ${p.question}` : ""}\nfields: none to fill — read the plan aloud and ask permission; the caller confirms (action "confirm") or cancels (action "cancel").`;
  }
  if (m.tool === "brief" && m.brief) {
    return `[Chat panel] CARD_APPEARED type=brief message_id=${m.id} title="Ringkasan Kebutuhan${m.brief.title ? ` · ${m.brief.title}` : ""}" model=${m.brief.model_label}\nfields: none to fill — the caller only confirms (action "confirm") or asks for changes (tell them to describe the change; you then relay it as a normal spoken request) or cancels.`;
  }
  if (m.pending_task?.workflow) {
    return `[Chat panel] CARD_APPEARED type=workflow message_id=${m.id} title="Langkah alur kerja"\nfields: run (true = jalankan sekarang, false = simpan sebagai draf). Steps and models can be left automatic.`;
  }
  if (m.pending_task) {
    return `[Chat panel] CARD_APPEARED type=task_offer message_id=${m.id} title="Tawaran tugas Ruang Kerja"\nfields: mode: single = terima beres, discuss = bahas dulu`;
  }
  return null;
}

export async function submitCard(cid, args) {
  const { message_id: mid, action, values = {} } = args || {};
  const r = await api.get(`/conversations/${cid}/messages`, { params: { limit: 30 } });
  const list = Array.isArray(r.data) ? r.data : r.data.messages || r.data.items || [];
  const m = list.find((x) => x.id === mid);
  if (!m) return { ok: false, error: "card not found (it may have been answered already)" };
  const pt = m.pending_tool;
  if (action === "cancel") {
    if (pt) await api.post(`/conversations/${cid}/messages/${mid}/cancel-tool`);
    else if (m.tool === "revision_plan" && m.plan) await api.post(`/tasks/${m.task_id || m.plan.task_id}/revision-plans/${m.plan.id}/cancel`);
    else if (m.tool === "brief") await api.post(`/conversations/${cid}/send`, { content: "Batal, tidak jadi dikerjakan." });
    return { ok: true, note: "cancelled" };
  }
  if (pt?.kind === "video") {
    await api.post(`/conversations/${cid}/messages/${mid}/run-tool`, null, { params: { choice: values.model, resolution: values.resolution, aspect: values.aspect, duration: values.duration, with_audio: values.with_audio, app_url: window.location.origin } });
    return { ok: true, note: "video rendering started; the result appears in the chat panel" };
  }
  if (pt) {
    await api.post(`/conversations/${cid}/messages/${mid}/run-tool`, null, { params: { choice: values.model, aspect: values.aspect, quality: values.quality, app_url: window.location.origin } });
    return { ok: true, note: "image generation started; the result appears in the chat panel" };
  }
  if (m.tool === "revision_plan" && m.plan) {
    const r = await api.post(`/tasks/${m.task_id || m.plan.task_id}/revision-plans/${m.plan.id}/approve`);
    const res = r.data?.result || {};
    return { ok: true, note: res.mode === "workflow" ? `revision approved; the workflow re-runs from stage ${res.first_step} — say it is running and the user will be notified` : `revision saved as version ${res.version}; summary: ${res.summary || "applied"}` };
  }
  if (m.tool === "brief") {
    await api.post(`/conversations/${cid}/send`, { content: "Kerjakan sesuai ringkasan.", brief_confirm: true });
    return { ok: true, note: "the specialist is working on the brief; the result appears in the chat panel" };
  }
  if (m.pending_task?.workflow) {
    await api.post(`/conversations/${cid}/workflow/accept`, { run: values.run !== false && values.run !== "false", batch: false, assignments: [] });
    return { ok: true, note: "workflow created in the Workspace" };
  }
  if (m.pending_task) {
    await api.post(`/conversations/${cid}/tasks/accept`, { mode: values.mode || "single", batch: false });
    return { ok: true, note: "task accepted in the Workspace" };
  }
  return { ok: false, error: "this card cannot be submitted by voice" };
}
